import { useEffect, useState } from "react";
import PageHero from "../../components/shared/PageHero";
import CtaBand from "../../components/shared/CtaBand";
import PortfolioUploader from "../../components/Portfolio/PortfolioUploader";
import PortfolioAnalysis from "../../components/Portfolio/PortfolioAnalysis";
import useReveal from "../../components/shared/useReveal";
import {
  normalizeManualHoldings,
  parsePortfolioFile,
} from "../../utils/portfolio/parseHoldings";
import { analyzePortfolio } from "../../utils/portfolio/analyzePortfolio";
import "./PortfolioReviewPage.css";

export default function PortfolioReviewPage() {
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [previewHoldings, setPreviewHoldings] = useState(null);
  const revealRef = useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleAnalyze = async (payload) => {
    setAnalyzing(true);
    setResult(null);
    setError("");

    try {
      let holdings = [];
      let warnings = [];

      if (payload.isManual) {
        holdings = normalizeManualHoldings(payload.holdings || []);
      } else if (payload.file) {
        const parsed = await parsePortfolioFile(payload.file);
        holdings = parsed.holdings;
        warnings = parsed.warnings || [];
        setPreviewHoldings(holdings);
      } else if (payload.holdings?.length) {
        holdings = payload.holdings;
      }

      if (!holdings.length) {
        throw new Error(
          "No holdings found. Check your file format or enter funds manually."
        );
      }

      // Brief pause so the loading UI feels intentional while we compute
      await new Promise((r) => setTimeout(r, 600));

      const analysis = analyzePortfolio(holdings, {
        fileName: payload.fileName,
        source: payload.source,
        warnings,
      });

      setMeta({
        fileName: payload.fileName,
        holdingsCount: holdings.length,
        source: payload.source,
        warnings,
        totalValue: analysis.totalValue,
      });
      setResult(analysis);

      window.setTimeout(() => {
        document.getElementById("analysis")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 80);
    } catch (err) {
      setError(err?.message || "Something went wrong while analysing.");
      setResult(null);
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <main className="pr-page">
      <PageHero
        badge="Portfolio Review"
        title="See how healthy your portfolio really is"
        subtitle="Upload CAMS, KFintech, PDF or Excel statements — or enter holdings manually — and get health score, risk, allocation, and actionable recommendations computed from your data."
        actions={[
          { label: "Upload Portfolio", to: "#upload", primary: true },
          { label: "Book a Call", to: "/contact" },
        ]}
      />

      <div ref={revealRef} className="pm-reveal">
        <PortfolioUploader
          onAnalyze={handleAnalyze}
          analyzing={analyzing}
          error={error}
          previewHoldings={previewHoldings}
          onClearPreview={() => setPreviewHoldings(null)}
        />
      </div>

      {analyzing && (
        <div className="pr-loading" aria-live="polite">
          <div className="pr-loading__orb" />
          <p>Parsing holdings &amp; running diagnostics…</p>
          <div className="pr-loading__bar">
            <span />
          </div>
        </div>
      )}

      {result && !analyzing && <PortfolioAnalysis data={result} meta={meta} />}

      <CtaBand
        title="Want a deeper expert review?"
        subtitle="Our advisors dig into overlap, tax lots, and exact XIRR from transaction history — book a free portfolio review today."
        primary={{ label: "Book Free Portfolio Review", to: "/contact" }}
        secondary={{ label: "Try Calculators", to: "/calculators" }}
      />
    </main>
  );
}
